// ─────────────────────────────────────────────────────────────────────────────
// ProductDetailPage.jsx───────────────────────────────────────────────────────────

import { useEffect } from 'react'
import { useParams, Navigate, Link } from 'react-router-dom'
import { getProductBySlug, getProductsByCategory } from '@data/products'
import { suppliers } from '@data/suppliers'
import { productCategories } from '@data/categories'
import PageHero from '@components/common/PageHero'
import CtaBanner from '@components/common/CtaBanner'
import SEOHead from '@components/common/SEOHead'

// ── Sector icons ──────────────────────────────────────────────────────────────
const SECTOR_ICONS = {
  Poultry: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
    </svg>
  ),
  Dairy: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <path d="M3 2l2.5 7H18.5L21 2" />
      <path d="M3 2h18M5.5 9C5.5 15 7 18 12 18s6.5-3 6.5-9" />
      <path d="M9 18v2m6-2v2" />
    </svg>
  ),
}

// ── White circle checkmark (on green bg) ─────────────────────────────────────
const CheckWhite = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
    <circle cx="10" cy="10" r="10" fill="rgba(255,255,255,0.25)" />
    <path d="M5.5 10L8.5 13L14.5 7"
      stroke="white" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

// ── Green checkmark ───────────────────────────────────────────────────────────
const CheckGreen = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
    <circle cx="10" cy="10" r="10" fill="#007935" />
    <path d="M5.5 10L8.5 13L14.5 7"
      stroke="white" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

// ── Related product card ──────────────────────────────────────────────────────
function RelatedCard({ product }) {
  const BG = 'linear-gradient(135deg, #2d5c3e, #3d7a55)'
  return (
    <Link to={`/products/${product.slug}`} style={{ textDecoration: 'none', display: 'flex', flexDirection: 'column' }}>
      <div style={{
        position: 'relative', aspectRatio: '4/3', overflow: 'hidden',
        background: product.image ? 'transparent' : BG, marginBottom: '12px',
      }}>
        {product.image && (
          <img src={product.image} alt={product.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        )}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '9px 12px', background: 'rgba(0,0,0,0.58)',
          fontFamily: "'DM Sans',sans-serif", fontSize: '13px', fontWeight: 500,
          color: '#ffffff',
        }}>
          <span>{product.name}</span>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
      </div>
      <p style={{
        fontFamily: "'DM Sans',sans-serif", fontSize: '13px', color: '#55544e',
        lineHeight: 1.6, margin: '0 0 10px',
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
      }}>
        {product.description}
      </p>
      <span style={{
        fontFamily: "'DM Sans',sans-serif", fontSize: '12.5px', fontWeight: 600,
        color: '#007935', display: 'inline-flex', alignItems: 'center', gap: '4px',
      }}>
        Read more ...
      </span>
    </Link>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function ProductDetailPage() {
  const { slug } = useParams()
  const product = getProductBySlug(slug)

  useEffect(() => { window.scrollTo(0, 0) }, [slug])

  if (!product) return <Navigate to="/products" replace />

  const supplier = suppliers.find(s => s.id === product.supplier)
  const category = productCategories.find(c => c.id === product.category)
  const related = getProductsByCategory(product.category)
    .filter(p => p.id !== product.id)
    .slice(0, 4)

  const BG = 'linear-gradient(135deg, #2d5c3e 0%, #3d7a55 100%)'

  return (
    <main>
      <SEOHead
        title={product.name}
        description={product.tagline ? `${product.tagline} ${product.description}`.slice(0, 155) : product.description.slice(0, 155)}
        path={`/products/${product.slug}`}
      />

      {/* ── 02 PageHero with breadcrumbs ── */}
      <PageHero
        title={product.name}
        breadcrumbs={[
          { label: 'Home', to: '/' },
          { label: 'Products', to: '/products' },
          { label: product.name },
        ]}
      />

      {/* ── 01 Full-width hero image ── 
      <div style={{
        width:      '100%',
        height:     'clamp(200px, 35vw, 400px)',
        overflow:   'hidden',
        position:   'relative',
        background: BG,
      }}>
        {product.image && (
          <img src={product.image} alt={product.name}
            style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
        )}
        <div style={{
          position:'absolute', inset:0,
          background:'linear-gradient(to top, rgba(0,0,0,0.35) 0%, transparent 60%)',
        }} />
      </div>*/}



      {/* ── 03 Main 2-col section ── */}
      <section style={{ background: '#ffffff', padding: '64px 0' }}>
        <div style={{ maxWidth: 'var(--container-max)', margin: '0 auto', padding: '0 var(--container-padding)' }}>
          <div
            className="product-detail-grid"
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '48px', alignItems: 'start' }}
          >

            {/* LEFT: name + tagline + description + icons */}
            <div>
              {/* Category label */}
              {category && (
                <p style={{
                  fontFamily: "'DM Sans',sans-serif", fontSize: '11px', fontWeight: 700,
                  letterSpacing: '0.10em', textTransform: 'uppercase',
                  color: '#007935', margin: '0 0 10px',
                }}>
                  {category.label}
                </p>
              )}

              {/* Product name — MIAVIT: large, green */}
              <h1 style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 'clamp(1.75rem, 3.5vw, 3rem)',
                fontWeight: 300, letterSpacing: '0.02em',
                color: '#007935', lineHeight: 1.1, margin: '0 0 10px',
              }}>
                {product.name}
              </h1>

              {/* Tagline */}
              {product.tagline && (
                <p style={{
                  fontFamily: "'DM Sans',sans-serif", fontSize: '1.0625rem',
                  fontWeight: 400, color: '#55544e', fontStyle: 'italic',
                  margin: '0 0 20px', lineHeight: 1.5,
                }}>
                  {product.tagline}
                </p>
              )}

              {/* Description */}
              <p style={{
                fontFamily: "'DM Sans',sans-serif", fontSize: '14.5px',
                color: '#55544e', lineHeight: 1.8, margin: '0 0 28px',
              }}>
                {product.description}
              </p>

              {/* Animal species icons — MIAVIT style */}
              <div>
                <p style={{
                  fontFamily: "'DM Sans',sans-serif", fontSize: '11px', fontWeight: 700,
                  letterSpacing: '0.10em', textTransform: 'uppercase',
                  color: '#88877f', margin: '0 0 12px',
                }}>
                  Target Species
                </p>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  {product.sectors.map(sector => (
                    <div key={sector} style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                      padding: '12px 16px',
                      background: 'rgba(0,121,53,0.06)',
                      border: '1px solid rgba(0,121,53,0.15)',
                      minWidth: '72px',
                    }}>
                      <div style={{ color: '#007935' }}>
                        {SECTOR_ICONS[sector] || (
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                            <circle cx="12" cy="12" r="10" />
                          </svg>
                        )}
                      </div>
                      <span style={{
                        fontFamily: "'DM Sans',sans-serif", fontSize: '11px',
                        fontWeight: 600, color: '#007935', textAlign: 'center',
                      }}>
                        {sector}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Supplier info */}
              {supplier && (
                <div style={{
                  marginTop: '28px', paddingTop: '20px',
                  borderTop: '1px solid #f0f0ee',
                  display: 'flex', alignItems: 'center', gap: '10px',
                }}>
                  <p style={{
                    fontFamily: "'DM Sans',sans-serif", fontSize: '11px', fontWeight: 700,
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                    color: '#88877f', margin: 0,
                  }}>
                    Supplied by:
                  </p>
                  <a href={supplier.website} target="_blank" rel="noopener noreferrer"
                    style={{
                      fontFamily: "'DM Sans',sans-serif", fontSize: '13.5px',
                      fontWeight: 600, color: '#007935', textDecoration: 'none',
                    }}>
                    {supplier.name} ({supplier.country})
                  </a>
                  {supplier.certifications?.length > 0 && (
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {supplier.certifications.map(c => (
                        <span key={c} style={{
                          fontFamily: "'DM Sans',sans-serif", fontSize: '10px',
                          fontWeight: 700, letterSpacing: '0.06em',
                          color: '#065e32', background: 'rgba(0,121,53,0.08)',
                          padding: '2px 7px', border: '1px solid rgba(0,121,53,0.2)',
                        }}>
                          {c}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* RIGHT: Green benefits checklist — MIAVIT red → our green */}
            <div style={{ background: '#007935', padding: '36px 32px' }}>
              <h3 style={{
                fontFamily: "'DM Sans',sans-serif",
                fontSize: 'clamp(1.1rem, 1.8vw, 1.375rem)',
                fontWeight: 400, letterSpacing: '0.04em', textTransform: 'uppercase',
                color: '#ffffff', margin: '0 0 24px',
              }}>
                Key Benefits
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {product.benefits.map((b, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <CheckWhite />
                    <span style={{
                      fontFamily: "'DM Sans',sans-serif", fontSize: '14px',
                      color: 'rgba(255,255,255,0.90)', lineHeight: 1.55,
                    }}>
                      {b}
                    </span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── 04 Packaging + Usage — MIAVIT product info section ── */}
      <section style={{ background: '#f5f5f3', padding: '56px 0' }}>
        <div style={{ maxWidth: 'var(--container-max)', margin: '0 auto', padding: '0 var(--container-padding)' }}>
          <div
            className="product-pkg-grid"
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '64px', alignItems: 'start' }}
          >

            {/* Product image placeholder */}
            <div style={{
              background: product.image ? 'transparent' : BG,
              aspectRatio: '4/3', overflow: 'hidden', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
            }}>
              {product.image
                ? <img src={product.image} alt={product.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                : (
                  <svg width="52" height="52" viewBox="0 0 24 24" fill="none"
                    stroke="rgba(255,255,255,0.25)" strokeWidth="1" strokeLinecap="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                )
              }
            </div>

            {/* Packaging + Usage info */}
            <div>
              <h2 style={{
                fontFamily: "'DM Sans',sans-serif",
                fontSize: 'clamp(1.25rem, 2vw, 1.625rem)',
                fontWeight: 400, letterSpacing: '0.04em', textTransform: 'uppercase',
                color: '#007935', margin: '0 0 24px',
              }}>
                Packaging & Usage
              </h2>

              {/* Pack size */}
              {product.packSize && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' }}>
                  <CheckGreen />
                  <div>
                    <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#88877f', margin: '0 0 3px' }}>
                      Pack Size
                    </p>
                    <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: '14.5px', fontWeight: 500, color: '#1a1a18', margin: 0 }}>
                      {product.packSize}
                    </p>
                  </div>
                </div>
              )}

              {/* Usage instructions */}
              {product.usage && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' }}>
                  <CheckGreen />
                  <div>
                    <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#88877f', margin: '0 0 3px' }}>
                      Usage
                    </p>
                    <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: '14px', color: '#55544e', lineHeight: 1.7, margin: 0 }}>
                      {product.usage}
                    </p>
                  </div>
                </div>
              )}

              {/* Other sizes note */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <CheckGreen />
                <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: '13.5px', color: '#55544e', lineHeight: 1.6, margin: 0 }}>
                  Other packaging sizes available on request. Contact our technical team for bulk pricing.
                </p>
              </div>

              {/* Contact button */}
              <div style={{ marginTop: '28px' }}>
                <Link
                  to="/contact"
                  style={{
                    display: 'inline-flex', alignItems: 'center',
                    fontFamily: "'DM Sans',sans-serif", fontSize: '13.5px', fontWeight: 600,
                    color: '#ffffff', textDecoration: 'none',
                    padding: '9px 22px', background: '#007935', border: 'none',
                    transition: 'background 0.2s ease',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#065e32' }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#007935' }}
                >
                  Enquire about this product
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── 05 Related products ── */}
      {related.length > 0 && (
        <section style={{ background: '#ffffff', padding: '56px 0' }}>
          <div style={{ maxWidth: 'var(--container-max)', margin: '0 auto', padding: '0 var(--container-padding)' }}>
            <h2 style={{
              fontFamily: "'DM Sans',sans-serif",
              fontSize: 'clamp(1.25rem, 2vw, 1.625rem)',
              fontWeight: 400, letterSpacing: '0.04em', textTransform: 'uppercase',
              color: '#007935', margin: '0 0 36px',
            }}>
              Related Products
            </h2>
            <div className="related-products-grid" style={{
              display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '28px', alignItems: 'start',
            }}>
              {related.map(p => (
                <RelatedCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── 06 CtaBanner ── */}
      <CtaBanner
        tag="Get Expert Advice"
        title="Questions about this product?"
        body="Our technical team is ready to discuss the right application and dosage for your specific livestock operation."
        buttonText="Contact Us"
        buttonTo="/contact"
      />

      <style>{`
        @media (max-width: 900px) {
          .product-detail-grid { grid-template-columns: 1fr !important; }
          .product-pkg-grid    { grid-template-columns: 1fr !important; gap: 32px !important; }
        }
        @media (max-width: 1100px) { .related-products-grid { grid-template-columns: repeat(3, 1fr) !important; } }
        @media (max-width: 767px)  { .related-products-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 480px)  { .related-products-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </main>
  )
}